import { useEffect, useMemo, useRef, useState } from 'react';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { extractListPhotos } from '../utils/result-image.utils';
import { TVehiculeCheckResultCheckPointSchema } from '@/schemas/vehicule-check-result/vehicule-check-check-point.schema';

export const useResultPhotoMenuEffect = (
  menuRef: React.RefObject<HTMLDivElement>,
) => {
  const [isPhotoHovered, setIsPhotoHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef, isMenuOpen]);

  return {
    isPhotoHovered,
    isMenuOpen,
    setIsPhotoHovered,
    setIsMenuOpen,
  };
};

export const usePreloadCheckpointImages = (
  isLoading: boolean,
  results: TVehiculeCheckResultCheckPointSchema[],
) => {
  const preloadedImagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isLoading && results?.length > 0) {
      // Preload all checkpoint images to improve accordion opening performance
      results.forEach((item) => {
        if (item?.checkPoint?.imageCheckPoint && !item?.checkPoint?.isVideo) {
          const imageUrl = `${R2_PUBLIC_DOMAIN}/${item.checkPoint.imageCheckPoint}`;

          // Skip if already preloaded
          if (preloadedImagesRef.current.has(imageUrl)) {
            return;
          }

          const img = new Image();
          img.onload = () => {
            // Mark as preloaded
            preloadedImagesRef.current.add(imageUrl);
          };
          img.onerror = () => {
            // Still mark as attempted to avoid retrying
            preloadedImagesRef.current.add(imageUrl);
          };
          img.src = imageUrl;
        }
      });
    }
  }, [isLoading, results, preloadedImagesRef]);
};

export const useResultImageList = (
  results: TVehiculeCheckResultCheckPointSchema[],
  property: 'equipments' | 'damages' | 'vehicules',
) => {
  return useMemo(
    () => extractListPhotos(results, property),
    [results, property],
  );
};
